import { useCallback, useEffect, useState } from 'react';

import type { PopulationInsightData } from '@/types/dashboard';
import { useAuth } from '@/hooks/auth/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('useDailyInsight');

const INSIGHT_STORAGE_PREFIX = 'last_insight_seen_';
const INSIGHT_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export interface DailyInsightState {
  insight: PopulationInsightData | null;
  isLoading: boolean;
  dismiss: () => void;
}

export function useDailyInsight(): DailyInsightState {
  const { user } = useAuth();
  const [insight, setInsight] = useState<PopulationInsightData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const lastSeen = localStorage.getItem(`${INSIGHT_STORAGE_PREFIX}${user.id}`);
        if (lastSeen && Date.now() - Number.parseInt(lastSeen, 10) < INSIGHT_COOLDOWN_MS) {
          return;
        }

        const { data, error } = await supabase
          .from('health_segment_insights')
          .select('insight_type, insight_text, impact_value, sample_size')
          .gte('confidence_score', 0.6)
          .order('updated_at', { ascending: false })
          .limit(10);

        if (error || !data || data.length === 0 || cancelled) return;

        const selected = data[Math.floor(Math.random() * data.length)];
        if (!selected.insight_text) return;

        setInsight({
          type: selected.insight_type,
          text: selected.insight_text,
          impact: selected.impact_value ?? undefined,
          sample_size: selected.sample_size ?? undefined,
        });
      } catch (error) {
        logger.error('Error fetching daily insight', { error });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const dismiss = useCallback(() => {
    if (user?.id) {
      localStorage.setItem(`${INSIGHT_STORAGE_PREFIX}${user.id}`, Date.now().toString());
    }
    setInsight(null);
  }, [user?.id]);

  return { insight, isLoading, dismiss };
}
